import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { resolveStoredAssetRef } from "@/features/image-generation";

export const PROJECT_COVERS_BUCKET = "project-covers";
export const ARTSTYLE_REFS_BUCKET = "artstyle-refs";
export const PROJECT_ARTSTYLE_REFERENCE_LIMIT = 5;

function sanitizeFileName(fileName: string) {
  return fileName.replace(/[^a-zA-Z0-9._-]+/g, "-");
}

function createStorageToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `ref-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase environment variables are not configured.");
  }
}

export function buildProjectCoverPath(userId: string, projectId: string) {
  return `${userId}/${projectId}`;
}

export async function uploadProjectCover(
  userId: string,
  projectId: string,
  file: File,
) {
  ensureConfigured();

  const path = buildProjectCoverPath(userId, projectId);

  const { data, error } = await supabase.storage
    .from(PROJECT_COVERS_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type || undefined,
      upsert: true,
    });

  if (error) {
    throw error;
  }

  return data.path;
}

export async function deleteProjectCover(path: string) {
  ensureConfigured();
  const resolved = resolveStoredAssetRef(path, PROJECT_COVERS_BUCKET);

  if (!resolved) {
    return;
  }

  const { error } = await supabase.storage
    .from(resolved.bucket)
    .remove([resolved.path]);

  if (error) {
    throw error;
  }
}

export async function createProjectCoverSignedUrl(path: string) {
  ensureConfigured();
  const resolved = resolveStoredAssetRef(path, PROJECT_COVERS_BUCKET);

  if (!resolved) {
    throw new Error("Project cover path is missing.");
  }

  const { data, error } = await supabase.storage
    .from(resolved.bucket)
    .createSignedUrl(resolved.path, 60 * 30);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}

export function buildProjectArtstyleReferencePath(
  userId: string,
  projectId: string,
  fileName: string,
) {
  return `${userId}/${projectId}/${createStorageToken()}-${sanitizeFileName(fileName)}`;
}

export async function uploadProjectArtstyleReference(
  userId: string,
  projectId: string,
  file: File,
) {
  ensureConfigured();
  const path = buildProjectArtstyleReferencePath(userId, projectId, file.name);

  const { data, error } = await supabase.storage
    .from(ARTSTYLE_REFS_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type || undefined,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return data.path;
}

export async function deleteProjectArtstyleReference(path: string) {
  ensureConfigured();
  const resolved = resolveStoredAssetRef(path, ARTSTYLE_REFS_BUCKET);

  if (!resolved) {
    return;
  }

  const { error } = await supabase.storage
    .from(resolved.bucket)
    .remove([resolved.path]);

  if (error) {
    throw error;
  }
}

export async function createProjectArtstyleReferenceSignedUrl(path: string) {
  ensureConfigured();
  const resolved = resolveStoredAssetRef(path, ARTSTYLE_REFS_BUCKET);

  if (!resolved) {
    throw new Error("Project artstyle reference path is missing.");
  }

  const { data, error } = await supabase.storage
    .from(resolved.bucket)
    .createSignedUrl(resolved.path, 60 * 30);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}
